import { useEffect, useRef } from "react";

import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

import { useAgentExecutionClient } from "src/agents/queries/execution";
import {
  copilotMessagesQueryBase,
  getSessionId,
  sessionKeys,
  TERMINAL_STATUSES,
} from "src/cases/copilot/useCaseCopilotSession";

/**
 * Polls the copilot session to drive the loading indicator on the copilot
 * button. Shares the same query key as `useCaseCopilotSession` so React
 * Query deduplicates requests.
 *
 * When `resetKey` changes (eager-mode trigger), writes an eager-loading
 * signal so `useCaseCopilotSession` can disable chat input immediately.
 */
const POLL_TIMEOUT_MS = 60_000;
const POLL_INTERVAL_MS = 1_000;

type UseCopilotSessionStatusParams = {
  userId: string | undefined;
  caseId: string;
  agentVersionId: string | undefined;
  enabled: boolean;
  resetKey?: number;
};

export const useCopilotSessionStatus = ({
  userId,
  caseId,
  agentVersionId,
  enabled,
  resetKey = 0,
}: UseCopilotSessionStatusParams): { isCopilotSessionLoading: boolean } => {
  const sessionId = getSessionId(userId, caseId, agentVersionId);
  const { client } = useAgentExecutionClient();
  const queryClient = useQueryClient();
  const mountTime = useRef(Date.now());

  const awaitingRef = useRef(false);
  const sawNonTerminalRef = useRef(false);
  const prevResetKeyRef = useRef(resetKey);
  // Distinguishes eager triggers from user-invoked sessions.
  const wasEagerTriggeredRef = useRef(false);

  // Reset polling state on eager-mode trigger and start timeout.
  useEffect(() => {
    if (!resetKey || resetKey === prevResetKeyRef.current) return;
    prevResetKeyRef.current = resetKey;
    mountTime.current = Date.now();
    sawNonTerminalRef.current = false;
    awaitingRef.current = true;
    wasEagerTriggeredRef.current = true;
    if (sessionId) {
      queryClient.setQueryData(sessionKeys.eagerLoading(userId, caseId), true);
      queryClient.invalidateQueries({
        queryKey: sessionKeys.messages(sessionId),
      });
    }

    const timeout = setTimeout(() => {
      awaitingRef.current = false;
      queryClient.setQueryData(sessionKeys.eagerLoading(userId, caseId), false);
      if (!sawNonTerminalRef.current) {
        wasEagerTriggeredRef.current = false;
      }
    }, POLL_TIMEOUT_MS);
    return () => {
      clearTimeout(timeout);
      queryClient.setQueryData(sessionKeys.eagerLoading(userId, caseId), false);
    };
  }, [resetKey, sessionId, queryClient, userId, caseId]);

  const queryBase = copilotMessagesQueryBase({
    sessionId,
    client,
    queryClient,
  });

  const query = useInfiniteQuery({
    ...queryBase,
    enabled: enabled && !!client && !!sessionId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;

      const sessionStatus = query.state.data?.pages?.[0]?.session?.status;

      // Running — keep polling; clear eager signal on first detection.
      if (sessionStatus && !TERMINAL_STATUSES.has(sessionStatus)) {
        sawNonTerminalRef.current = true;
        if (awaitingRef.current) {
          awaitingRef.current = false;
          queryClient.setQueryData(
            sessionKeys.eagerLoading(userId, caseId),
            false,
          );
        }
        return POLL_INTERVAL_MS;
      }

      // Running → terminal — done; clear eager trigger flag.
      if (sawNonTerminalRef.current) {
        wasEagerTriggeredRef.current = false;
        sawNonTerminalRef.current = false;
        return false;
      }

      // Awaiting new session after trigger — poll within timeout.
      if (awaitingRef.current) {
        if (Date.now() - mountTime.current <= POLL_TIMEOUT_MS) {
          return POLL_INTERVAL_MS;
        }
        return false;
      }

      // Mount: stop on terminal, poll if no session yet.
      if (sessionStatus && TERMINAL_STATUSES.has(sessionStatus)) return false;
      if (Date.now() - mountTime.current <= POLL_TIMEOUT_MS) {
        return POLL_INTERVAL_MS;
      }

      return false;
    },
    select: (data) => data.pages[0]?.session,
  });

  const sessionStatus = query.data?.status;
  const isNonTerminal =
    sessionStatus != null && !TERMINAL_STATUSES.has(sessionStatus);

  return {
    isCopilotSessionLoading: isNonTerminal && wasEagerTriggeredRef.current,
  };
};
